import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calculator, ChevronRight } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { PURITY_MULTIPLIERS } from '@/lib/constants';
import { FALLBACK_METAL_PRICES } from '@/server/config/ssr-config';

interface CalcolatoreOroUsatoProps {
  ssrMetalPrices?: any;
}

const KARAT_OPTIONS = [
  { label: '24KT (oro puro 999‰)', value: '24KT', mult: PURITY_MULTIPLIERS['24KT'] },
  { label: '22KT (sterline, marenghi)', value: '22KT', mult: PURITY_MULTIPLIERS['22KT'] },
  { label: '20KT', value: '20KT', mult: PURITY_MULTIPLIERS['20KT'] },
  { label: '18KT (gioielleria classica 750‰)', value: '18KT', mult: PURITY_MULTIPLIERS['18KT'] },
  { label: '14KT (gioielleria moderna 585‰)', value: '14KT', mult: PURITY_MULTIPLIERS['14KT'] },
  { label: '9KT (gioielli leggeri 375‰)', value: '9KT', mult: PURITY_MULTIPLIERS['9KT'] },
];

export default function CalcolatoreOroUsato({ ssrMetalPrices }: CalcolatoreOroUsatoProps) {
  const { currentGoldPrice } = useStore();
  const [pesoStr, setPesoStr] = useState('5');
  const [karat, setKarat] = useState('18KT');
  const [spreadStr, setSpreadStr] = useState('10');

  const goldPrice24K =
    currentGoldPrice?.price ??
    ssrMetalPrices?.gold?.price ??
    FALLBACK_METAL_PRICES.gold.price;

  const result = useMemo(() => {
    const peso = parseFloat(pesoStr.replace(',', '.')) || 0;
    const spread = parseFloat(spreadStr.replace(',', '.')) || 0;
    const k = KARAT_OPTIONS.find((o) => o.value === karat) ?? KARAT_OPTIONS[3];
    const pricePerGram24K = goldPrice24K;
    const pricePerGramKaratura = pricePerGram24K * k.mult;
    const valoreOroFino = pricePerGramKaratura * peso;
    const valoreCompro = valoreOroFino * (1 - spread / 100);
    return {
      pricePerGramKaratura,
      valoreOroFino,
      valoreCompro,
      risparmioSpread: valoreOroFino - valoreCompro,
    };
  }, [pesoStr, karat, spreadStr, goldPrice24K]);

  const fmt = (v: number) =>
    v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const canonical = 'https://www.gildy.it/calcolatore-oro-usato';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Calcolatore Oro Usato Gildy',
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    description:
      "Calcolatore gratuito del valore dell'oro usato in base a peso, caratura e spread di mercato. Quotazione spot LBMA aggiornata.",
    url: canonical,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
  };

  return (
    <>
      <Helmet>
        <title>Calcolatore Oro Usato Online | Quanto Vale Oggi | Gildy</title>
        <meta
          name="description"
          content="Calcola in tempo reale il valore del tuo oro usato: inserisci peso e caratura, ottieni il prezzo aggiornato al spot LBMA. Confronta con i compro oro vicino a te."
        />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:title" content="Calcolatore Oro Usato Online | Gildy" />
        <meta
          property="og:description"
          content="Quanto vale il tuo oro? Calcolatore gratuito basato sul prezzo spot LBMA aggiornato."
        />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="it_IT" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <nav className="flex items-center text-sm text-muted-foreground mb-4">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-4 w-4 mx-1" />
          <span className="text-foreground">Calcolatore oro usato</span>
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
          Calcolatore oro usato online
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8">
          Inserisci <strong>peso</strong> e <strong>caratura</strong> del tuo oggetto:
          ottieni una stima del valore al prezzo spot internazionale (LBMA) e dell'importo che
          ti pagherebbe un compro oro applicando lo spread medio di mercato.
        </p>

        <Card className="border-gold/30 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-gold" />
              Calcolatore
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label htmlFor="peso">Peso in grammi</Label>
              <Input
                id="peso"
                type="number"
                inputMode="decimal"
                step="0.1"
                min="0"
                value={pesoStr}
                onChange={(e) => setPesoStr(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="karat">Caratura</Label>
              <select
                id="karat"
                value={karat}
                onChange={(e) => setKarat(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {KARAT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="spread">Spread compro oro (%)</Label>
              <Input
                id="spread"
                type="number"
                inputMode="decimal"
                step="0.5"
                min="0"
                max="30"
                value={spreadStr}
                onChange={(e) => setSpreadStr(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Spread medio in Italia: 5-15% per gioielli, 3-8% per monete e lingotti.
              </p>
            </div>

            <div className="border-t pt-4 space-y-2">
              <p className="text-sm text-muted-foreground">
                Prezzo spot oro 24kt: <strong>{fmt(goldPrice24K)} €/g</strong>
              </p>
              <p className="text-sm text-muted-foreground">
                Prezzo {karat}: <strong>{fmt(result.pricePerGramKaratura)} €/g</strong>
              </p>
              <p className="text-base">
                Valore oro fino: <strong>{fmt(result.valoreOroFino)} €</strong>
              </p>
              <p className="text-2xl font-bold text-gold">
                Compro oro ti pagherebbe: {fmt(result.valoreCompro)} €
              </p>
              <p className="text-xs text-muted-foreground">
                (spread trattenuto: {fmt(result.risparmioSpread)} €)
              </p>
            </div>
          </CardContent>
        </Card>

        <section className="mt-10 space-y-4 text-muted-foreground">
          <h2 className="text-2xl font-bold text-foreground">Come funziona il calcolo</h2>
          <p>
            Il valore del tuo oro si ottiene moltiplicando il <strong>peso netto</strong> per il{' '}
            <strong>prezzo spot</strong> dell'oro 24kt e per il{' '}
            <strong>moltiplicatore di caratura</strong> (es. 0.75 per il 18kt, 0.585 per il 14kt).
            Il compro oro applica poi uno spread che copre costi di fusione, analisi e margine
            operativo.
          </p>
          <p>
            Su Gildy puoi <Link to="/compro-oro-vicino-a-me" className="text-gold underline">trovare i compro oro vicino a te</Link>{' '}
            con spread trasparente e confrontare l'offerta reale prima di vendere.
          </p>
        </section>
      </div>
    </>
  );
}
